'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AdBanner from '@/components/AdBanner';
import { createClient } from '@/lib/supabase/client';
import {
  fetchAuctions,
  fetchLots,
  money,
  timeLeft,
  type Auction,
  type AuctionLot,
} from '@/lib/auctions';

/**
 * The auctions front page: sales that are on, coming up, and finished.
 *
 * Replaced six lots from "Test Seller" with live-looking bids and countdowns —
 * a bidding floor that did not exist. If there are no real sales this says so.
 */
export default function TimedAuctionsScreen() {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [lots, setLots] = useState<AuctionLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let live = true;
    const supabase = createClient();
    (async () => {
      try {
        const sales = await fetchAuctions(supabase, { status: ['scheduled', 'live', 'closed'] });
        if (!live) return;
        setAuctions(sales);
        if (sales.length) {
          const all = await Promise.all(
            sales.map((s) => fetchLots(supabase, { auctionId: s.id, status: ['live', 'sold', 'unsold'] }))
          );
          if (live) setLots(all.flat());
        }
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Could not load the auctions.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const lotsBySale = useMemo(() => {
    const m = new Map<string, AuctionLot[]>();
    for (const l of lots) {
      if (!m.has(l.auction_id)) m.set(l.auction_id, []);
      m.get(l.auction_id)!.push(l);
    }
    return m;
  }, [lots]);

  const live = auctions.filter((a) => a.status === 'live');
  const upcoming = auctions.filter((a) => a.status === 'scheduled');
  const past = auctions.filter((a) => a.status === 'closed');

  return (
    <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-extrabold text-foreground">Timed Auctions</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
          Online timed sales for livestock, machinery and deadstock. Bidding is open around the
          clock — no need to be anywhere at a particular time.
        </p>
      </div>

      {/* The rule, stated before anyone bids */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 mb-8">
        <Icon name="ClockIcon" size={20} className="text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          <span className="font-bold">No last-second sniping.</span> If a bid comes in during the
          final five minutes of a lot, that lot stays open for another five minutes. It keeps going
          until nobody raises the bid — so you never lose a lot to someone who bid two seconds
          before the end.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6" role="alert">
          <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" aria-hidden="true">
          {[0, 1].map((i) => <div key={i} className="card h-48 animate-pulse bg-muted/40" />)}
        </div>
      ) : auctions.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
            <Icon name="ClockIcon" size={30} className="text-muted-foreground" />
          </div>
          <h2 className="text-lg font-bold text-foreground mb-3">No sales scheduled yet</h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Our first timed sale is being put together. Nothing is listed until there are real lots
            taking real bids.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Link href="/marketplace-listings" className="btn-primary text-sm">
              <Icon name="Square3Stack3DIcon" size={16} />
              Browse the marketplace
            </Link>
            <Link href="/contact" className="btn-secondary text-sm">
              <Icon name="EnvelopeIcon" size={16} />
              Tell us what you would enter
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          {[
            { label: 'Bidding now', sales: live, tone: 'text-green-700' },
            { label: 'Coming up', sales: upcoming, tone: 'text-blue-700' },
            { label: 'Finished', sales: past, tone: 'text-muted-foreground' },
          ]
            .filter((g) => g.sales.length > 0)
            .map((group) => (
              <section key={group.label}>
                <h2 className={`text-lg font-bold mb-4 ${group.tone}`}>{group.label}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {group.sales.map((sale) => {
                    const saleLots = lotsBySale.get(sale.id) ?? [];
                    const openLots = saleLots.filter((l) => l.status === 'live');
                    const soonest = openLots
                      .slice()
                      .sort((a, b) => new Date(a.closes_at).getTime() - new Date(b.closes_at).getTime())[0];
                    return (
                      <Link
                        key={sale.id}
                        href={`/timed-auctions/${sale.id}`}
                        className="card listing-card-hover flex flex-col gap-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-bold text-foreground leading-snug">{sale.title}</h3>
                            {sale.category && (
                              <span className="inline-block mt-1.5 bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium">
                                {sale.category}
                              </span>
                            )}
                          </div>
                          {sale.status === 'live' && (
                            <span className="status-open shrink-0 text-xs font-bold px-2 py-0.5 rounded-full">
                              LIVE
                            </span>
                          )}
                        </div>

                        {sale.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{sale.description}</p>
                        )}

                        <dl className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <dt className="text-xs text-muted-foreground">Lots</dt>
                            <dd className="font-bold text-foreground">
                              {sale.status === 'closed' ? saleLots.length : openLots.length}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-muted-foreground">
                              {sale.status === 'live' ? 'First lot closes' :
                               sale.status === 'scheduled' ? 'Opens' : 'Closed'}
                            </dt>
                            <dd className="font-bold text-foreground">
                              {sale.status === 'live' && soonest
                                ? timeLeft(soonest.closes_at, now)
                                : new Date(sale.status === 'scheduled' ? sale.opens_at : sale.closes_at)
                                    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </dd>
                          </div>
                        </dl>

                        {sale.status === 'closed' && saleLots.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {saleLots.filter((l) => l.status === 'sold').length} sold ·{' '}
                            {saleLots.filter((l) => l.status === 'unsold').length} unsold
                          </p>
                        )}

                        <span className="mt-auto pt-2 text-sm font-semibold text-primary">
                          {sale.status === 'live' ? 'View lots and bid →' : 'View catalogue →'}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      )}

      <div className="mt-10">
        <AdBanner width={728} height={90} label="728x90 Leaderboard Banner" className="mx-auto rounded-lg" />
      </div>
    </main>
  );
}
